import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule } from '@angular/material/menu';
import { HIDDEN_ROLES } from '@core/constants/role-layout.constant';
import { installationZone, zonedInstant } from '@core/helpers/slot-series.helper';
import { DeviceEnergyflow, Installation, Point } from '@core/models';
import { TableColumnConfig } from '@core/models/skeleton.model';
import { gunzipCsvUtil } from '@core/utils/gunzip-csv.util';
import { OutlinedButtonComponent } from '@custom-components/general/button';
import { TableComponent } from '@custom-components/general/table/table.component';
import {
  energyflowDeviceIds,
  pvForecastPoint,
} from '@custom-components/sections/installation-insights/device-energyflow';
import { heatpumpSeries } from '@custom-components/sections/installation-insights/heatpump-series';
import { alignPvForecast } from '@custom-components/sections/installation-insights/pv-forecast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DeviceEnergyflowStore } from '@root/app/store/device-energyflow.store';
import { InstallationTelemetryStore } from '@root/app/store/installation-telemetry.store';
import { ExplorerForecast, explorerColumns, withUnit } from './explorer-columns';

const STORAGE_PREFIX = 'INSTALLATION_EXPLORER_COLUMNS:';
const COLUMN_WIDTH = '11rem';
const TIME_COLUMN_WIDTH = '9rem';

// The slot's own length decides how much of its time the first column shows: an hour or a quarter
// is told apart by the clock, a day or a week by the date, a month by the month. The download keeps
// the date on the clock formats, because it has no heading saying which day those slots belong to.
interface TimeFormat {
  table: string;
  download: string;
}

const TIME_FORMATS: Record<string, TimeFormat> = {
  'quarter-hour': { table: 'HH:mm', download: 'YYYY-MM-DD HH:mm' },
  hour: { table: 'HH:mm', download: 'YYYY-MM-DD HH:mm' },
  week: { table: 'YYYY-MM-DD', download: 'YYYY-MM-DD' },
  day: { table: 'YYYY-MM-DD', download: 'YYYY-MM-DD' },
  month: { table: 'YYYY-MM', download: 'YYYY-MM' },
};

const FALLBACK_TIME_FORMAT: TimeFormat = {
  table: 'YYYY-MM-DD HH:mm',
  download: 'YYYY-MM-DD HH:mm',
};

export function readColumnSelection(installationOid: string): string[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + installationOid);
    return raw ? (JSON.parse(raw) as string[]) : null;
  } catch {
    return null;
  }
}

function writeColumnSelection(installationOid: string, pointIds: string[]): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + installationOid, JSON.stringify(pointIds));
  } catch {
    // The selection is a convenience; without storage it simply resets next visit.
  }
}

// A spreadsheet runs a cell that starts with one of these as a formula, and the header carries
// each point's name. A leading quote keeps such a cell text; a negative number stays a number.
const FORMULA_START = /^[=+\-@\t\r]/;
const NUMBER = /^-?\d+(\.\d+)?$/;

export function csvCell(value: string): string {
  const text = FORMULA_START.test(value) && !NUMBER.test(value) ? `'${value}` : value;
  return /[",\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

@Component({
  selector: 'xemex-installation-data-table',
  imports: [
    MatCheckboxModule,
    MatMenuModule,
    OutlinedButtonComponent,
    TableComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installation-data-table.component.html',
})
export class InstallationDataTableComponent {
  installation = input.required<Installation>();

  readonly telemetryStore = inject(InstallationTelemetryStore);
  private readonly energyflowStore = inject(DeviceEnergyflowStore);
  private readonly translate = inject(TranslateService);

  // The response carries hidden points too; the picker offers them and the default leaves them out.
  // The heat pump series no catalogue role carries follow the response's own points.
  readonly points = computed<Point[]>(() => {
    const telemetry = this.telemetryStore.telemetry();
    const heatpump = heatpumpSeries(
      this.installation().points ?? [],
      deviceId => this.energyflowFor(deviceId),
      telemetry,
      key => this.translate.instant(key),
    );
    return [...(telemetry?.points ?? []), ...heatpump.telemetry].filter(
      point => !HIDDEN_ROLES.has(point.role),
    );
  });
  private readonly storedSelection = signal<string[] | null>(null);

  // An empty selection is the user's own choice and is kept. Only a selection that was never made,
  // or that names none of the points the installation still has, falls back to the active points.
  readonly selected = computed<string[]>(() => {
    const stored = this.storedSelection();
    const available = new Set(this.points().map(point => point.pointId));
    const kept = stored?.filter(pointId => available.has(pointId)) ?? [];
    if (stored !== null && (stored.length === 0 || kept.length > 0)) {
      return kept;
    }
    return this.points()
      .filter(point => point.state === 'active')
      .map(point => point.pointId);
  });

  readonly selectedPoints = computed(() =>
    this.points().filter(point => this.selected().includes(point.pointId)),
  );

  // Counts what the Columns menu lists and ticks.
  readonly columnCount = computed(() => ({
    shown: this.selectedPoints().length,
    available: this.points().length,
  }));

  readonly timezone = computed(() =>
    installationZone(this.installation().timezone, this.telemetryStore.telemetry()?.timezone),
  );

  private readonly forecast = computed<ExplorerForecast | null>(() => {
    const pvPoint = pvForecastPoint(this.installation().points ?? []);
    const deviceId = pvPoint?.binding?.source.deviceId;
    if (!pvPoint || deviceId === null || deviceId === undefined) return null;
    const series = alignPvForecast(
      this.energyflowFor(String(deviceId)),
      this.telemetryStore.telemetry(),
    );
    return series ? { pointId: pvPoint.pointOid, series } : null;
  });

  private readonly slotTimes = computed(() => this.points()[0]?.slots.map(slot => slot.time) ?? []);

  private readonly dataColumns = computed(() =>
    explorerColumns(
      this.selectedPoints(),
      this.points(),
      this.slotTimes().length,
      {
        meterReading: name => this.translate.instant('installations.data.meterReading', { name }),
        phaseTotal: this.translate.instant('installations.insights.activePowerTotal'),
        forecastWeather: this.translate.instant('installations.insights.forecast.weather'),
        forecastClearSky: this.translate.instant('installations.insights.forecast.clearSky'),
      },
      this.forecast(),
    ),
  );

  readonly columns = computed<TableColumnConfig[]>(() => [
    {
      field: 'time',
      label: this.timeLabel(),
      subLabel: this.timezone(),
      tooltip: this.timeHeader(),
      width: TIME_COLUMN_WIDTH,
      bold: true,
      sticky: true,
    },
    ...this.dataColumns().map(column => ({
      field: column.field,
      label: column.name,
      subLabel: column.detail,
      tooltip: column.label,
      width: COLUMN_WIDTH,
    })),
  ]);

  private readonly timeFormat = computed(
    () => TIME_FORMATS[this.telemetryStore.telemetry()?.granularity ?? ''] ?? FALLBACK_TIME_FORMAT,
  );

  readonly rows = computed(() => {
    const columns = this.dataColumns();
    return this.slotTimes().map((time, index) => {
      const row: Record<string, string> = {
        time: zonedInstant(time, this.timezone(), this.timeFormat().table),
      };
      for (const column of columns) {
        row[column.field] = column.values[index];
      }
      return row;
    });
  });

  readonly periodLabel = computed(() => this.telemetryStore.telemetry()?.period ?? '');

  private energyflowFor(deviceId: string): DeviceEnergyflow | null {
    return this.energyflowStore.responseFor(
      deviceId,
      this.telemetryStore.selectedPeriod(),
      this.telemetryStore.telemetryPeriod(),
    );
  }

  constructor() {
    effect(() => {
      this.storedSelection.set(readColumnSelection(this.installation().installationOid));
    });

    effect(() => {
      const deviceIds = energyflowDeviceIds(this.installation().points ?? []);
      const period = this.telemetryStore.selectedPeriod();
      if (deviceIds.length > 0) {
        untracked(() => this.energyflowStore.load({ deviceIds, period }));
      }
    });
  }

  isSelected(point: Point): boolean {
    return this.selected().includes(point.pointId);
  }

  toggle(point: Point): void {
    const next = this.isSelected(point)
      ? this.selected().filter(pointId => pointId !== point.pointId)
      : [...this.selected(), point.pointId];
    this.storedSelection.set(next);
    writeColumnSelection(this.installation().installationOid, next);
  }

  // Exports the table on screen: the same columns, rows and formatting, with the date kept on
  // the time of an hourly or quarter-hourly slot and each column under its full header.
  downloadCsv(): void {
    const columns = [
      { field: 'time', label: this.timeHeader() },
      ...this.dataColumns().map(({ field, label }) => ({ field, label })),
    ];
    const times = this.slotTimes().map(time =>
      zonedInstant(time, this.timezone(), this.timeFormat().download),
    );
    const lines = [
      columns.map(column => csvCell(column.label)).join(','),
      ...this.rows().map((row, index) =>
        columns
          .map(column =>
            csvCell(column.field === 'time' ? times[index] : (row[column.field] ?? '')),
          )
          .join(','),
      ),
    ];
    gunzipCsvUtil.triggerBlobDownload(
      new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' }),
      `${this.installation().name}-${this.periodLabel()}.csv`,
    );
  }

  pointHeader(point: Point): string {
    return withUnit(point.name, point.unit);
  }

  private timeLabel(): string {
    return this.translate.instant('installations.data.time');
  }

  private timeHeader(): string {
    return `${this.timeLabel()} (${this.timezone()})`;
  }
}
